<template>
  <a
    :class="[
      'card-item',
      {
        'card-item--checked': data?.is_star_personal,
        'card-item--mouseover': mouseover,
        'card-item--vertical':
          mode === 'free' && (resolution === 'vertical' || data?.resolution === '1080x1920'),
      },
    ]"
    :href="link || `/template/${data?.id}.html`"
    @click.prevent
    @mouseenter.self="onMouseenter"
    @mouseleave.self="onMouseleave"
  >
    <div class="card-item__cover" @click.stop.prevent="handleCoverClick">
      <slot name="cover">
        <Resource
          v-if="!data?.cover_url && !data?.map_image_url"
          :src="PLACEHOLDER"
          class="card-item__image card-item__image--cover"
        />
        <Resource
          :src="data?.cover_url"
          :alt="data.name"
          class="card-item__image"
          :class="
            data.resolution === '1080x1920'
              ? 'card-item__image--contain'
              : 'card-item__image--cover'
          "
          :title="data.name"
          :transform-resource="
            url => {
              return transformImage(url, autoScale ? { width: 600 } : { height: 600 });
            }
          "
        />
        <video
          v-if="shouldShowVideo"
          :src="data?.hover_video || data?.preview_video"
          autoplay
          loop
          muted
          playsinline
          :class="[
            'card-item__video',
            data?.resolution === '1080x1920'
              ? 'card-item__video--contain'
              : 'card-item__video--cover',
          ]"
        />

        <iconpark-icon
          v-if="showStaticCover"
          name="bofang"
          class="card-item__play-icon"
        ></iconpark-icon>
        <div v-if="showStaticCover" class="card-item__tags">
          <XTag v-if="data?.is_hot" type="hot" size="large" />
          <XTag v-if="data?.is_new" type="new" size="large" />
        </div>
        <div v-if="share && isChinese() && mouseover" class="card-item__collect">
          <Collect
            type="default"
            :model-value="data?.is_star_personal"
            :data="data"
            :params="{
              model_id: data?.id || -1,
              model_type: COLLECTION_MAP.PIECES_TEMPLATE,
              source: COLLECTION_SOURCE.YOUYAN,
            }"
            @update:model-value="v => $emit('change', ['is_star_personal', v])"
          />
        </div>
        <div v-if="share && isChinese()" v-show="mouseover" class="card-item__share">
          <TemplateShare
            :isCard="true"
            :trackParams="{
              target: TARGET_FROM.Template,
              dataId: data?.id,
              dataTitle: data?.title,
            }"
            :params="{
              share_ids: [data?.id],
              model_type: COLLECTION_MAP.PIECES_TEMPLATE,
            }"
            :templateId="data?.id"
            :linkUrl="`${location?.origin}/template/${data?.id}.html`"
            @click.stop
          />
        </div>
      </slot>
    </div>
    <div class="card-item__info" @click.stop>
      <template v-if="hover && mouseover && data && !previewing">
        <div
          v-if="isChinese()"
          class="card-item__actions"
          :class="{
            'card-item__actions--small': small,
          }"
        >
          <a-button
            v-if="showPreview"
            class="card-item__preview-btn"
            @click.stop.prevent="handlePreviewClick"
          >
            {{ $t('yu-lan') }}
          </a-button>
          <AxButton
            v-show="showCreate"
            type="primary"
            class="card-item__create-btn"
            :disabled="data?.copyright_limited"
            @click.stop.prevent="() => useTemplate(data, action)"
          >
            <div
              class="card-item__create-content"
              :data-buried="`ButtonUseTemplate`"
              :data-buried-params="{
                template_id: data?.id,
              }"
            >
              <iconpark-icon name="ai" size="16" class="card-item__create-icon"></iconpark-icon>
              <span class="card-item__create-text">
                {{ data?.copyright_limited ? $t('zhi-du-mo-ban') : $t('chuang-zuo-tong-kuan') }}
              </span>
            </div>
          </AxButton>
        </div>
        <CardAction
          v-else
          class="card-item__card-action"
          :leftTip="$t('yu-lan')"
          :rightText="$t('chuang-zuo-tong-kuan')"
          :showLeft="showCreate"
          :showRight="showPreview"
          :onClickLeft="handlePreviewClick"
          :onClickRight="() => useTemplate(data, action)"
        >
          <template #leftIcon>
            <iconpark-icon name="preview"></iconpark-icon>
          </template>
        </CardAction>
      </template>

      <slot v-else-if="!hideInfo" name="info">
        <div class="card-item__name" @click.prevent="() => handleClick()">
          {{ data?.display_name || data?.name }}
        </div>
      </slot>
    </div>
  </a>
</template>

<script lang="ts" setup>
import Collect from '@/features/collect/index.vue';
import type { Template } from '~/api/interface';
import CardAction from '~/components/card-action/index.vue';
import XTag from '~/components/xTag/index.vue';
import { PageKey } from '~/constants/page';
import { useTemplate } from '~/features/template/components/info';
import { TemplateShare } from './templateShare';

const props = withDefaults(
  defineProps<{
    data?: Template;
    link?: string;
    model_type?: COLLECTION_MAP; // 如果传了用传的，否则用data里面的model_type
    collect?: boolean;
    share?: boolean;
    autoScale?: boolean;
    hover?: boolean;
    small?: boolean;
    resolution?: 'vertical' | 'horizontal';
    action?: any;
    source?: TEMPLATE_SOURCE;
    showPreview?: boolean;
    showCreate?: boolean;
    hideInfo?: boolean;
    previewing?: boolean;
    mode?: 'free' | 'default'; // free是按照模板自己的横竖屏比例展示，default是统一按照横屏展示
  }>(),
  {
    isTemplate: true,
    collect: true,
    share: true,
    hover: true,
    small: false,
    data: undefined,
    action: undefined,
    showPreview: true,
    showCreate: true,
    previewing: false,
    mode: 'default',
  },
);

const emits = defineEmits(['change', 'cover-click', 'preview-click']);

const onCancel = inject('onCancel', () => {}) as () => void;

const handleClick = () => {
  if (props.data) {
    onCancel?.();
    const router = useRouter();
    router.push({
      name: PageKey.PERSONAL_PAGE,
      params: {
        key: props.data.homepage_org_id || 0,
      },
    });
    const open = useTemplateVisible();
    open.value = false;
  }
};

const showVideo = ref(false);
const mouseover = ref(false);
const shouldShowVideo = computed(() => showVideo.value || props.previewing);
const showStaticCover = computed(() => !mouseover.value && !props.previewing);
let timer: any = null;

const location = computed(() => {
  if (import.meta.client) {
    return window.location;
  }

  return null;
});

const handleCoverClick = () => {
  emits('cover-click', props.data);
};

const handlePreviewClick = () => {
  emits('preview-click', props.data);
};

const onMouseenter = () => {
  mouseover.value = true;
  if (timer) {
    window.clearTimeout(timer);
  }
  timer = setTimeout(() => {
    showVideo.value = true;
  }, 300);
};

const onMouseleave = () => {
  showVideo.value = false;
  mouseover.value = false;
  clearTimeout(timer);
  timer = null;
};
</script>

<style lang="scss" scoped>
:global(.card-tip .ant-tooltip-inner) {
  padding: 12px 16px;
  line-height: 18px;
  color: var(--x-text-1);
  box-shadow: 0px 2px 10px 0px rgba(9, 5, 48, 0.15);
}

.card-item {
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: content-box;
  outline: 0;
  overflow: hidden;
  transition: transform 0.3s ease-in-out;
}

.card-item--vertical .card-item__cover {
  padding-top: 177.78%;
}

.card-item:hover .card-item__play-icon {
  display: none;
}

.card-item:hover .card-item__actions {
  opacity: 1;
  backface-visibility: hidden;
}

.card-item__cover {
  position: relative;
  border-radius: 12px;
  padding-top: 56.25%;
  background: var(--x-back-4);
  overflow: hidden;
  cursor: pointer;
}

.card-item__image,
.card-item__video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.card-item__image--cover,
.card-item__video--cover {
  object-fit: cover;
}

.card-item__image--contain,
.card-item__video--contain {
  object-fit: contain;
}

.card-item__play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 28px;
  z-index: 10;
}

.card-item__tags {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  gap: 4px;
}

.card-item__collect {
  position: absolute;
  top: 12px;
  right: 12px;
}

.card-item__share {
  position: absolute;
  top: 12px;
  right: 52px;
}

.card-item__info {
  position: relative;
  display: flex;
  height: 32px;
  margin-top: 16px;
}

.card-item__actions {
  display: flex;
  justify-content: center;
  width: 100%;
  gap: 8px;
  opacity: 0;
  z-index: 25;
}

.card-item__actions--small {
  padding: 0 14px;
  gap: 12px;
}

.card-item__actions--small .ant-btn-lg {
  height: 30px;
  padding: 3px;
  font-size: 13px;
}

.card-item__preview-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 81;
  width: 100%;
  height: 32px;
  line-height: 1;
  font-size: 14px;
  font-weight: var(--x-weight-medium, 500);
}

.card-item__create-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 117;
  width: 100%;
  height: 32px;
  font-size: 14px;
  font-weight: var(--x-weight-medium, 500);

  box-shadow: none;
}

.card-item__create-content {
  display: flex;
  align-items: center;
  gap: 2px;
}

.card-item__create-icon {
  color: var(--x-white-1);
}

.card-item__create-text {
  font-size: 14px;
  line-height: 20px;
}

.card-item__card-action {
  width: 100%;
}

.card-item__name {
  color: var(--x-text-1);
  width: 100%;
  font-size: 16px;
  font-weight: var(--x-weight-medium, 500);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  user-select: none;
  text-align: center;
}
</style>
